import { useCallback } from 'react';
import { usePlayerStore } from '@/store/playerStore';
import { loadSubtitleFile, getActiveCue } from '@/services/subtitleService';
import { parseSRT } from '@/lib/utils';
import { parseVTT } from '@/services/subtitleService';

export function useSubtitles() {
  const { setSubtitleCues, setActiveSubtitle, subtitleCues, subtitleEnabled, subtitleOffset } =
    usePlayerStore();

  const loadFile = useCallback(
    async (file: File): Promise<string> => {
      const cues = await loadSubtitleFile(file);
      setSubtitleCues(cues);

      // Return VTT string for sharing with guests
      const text = await file.text();
      const lowerName = file.name.toLowerCase();
      if (lowerName.endsWith('.srt')) {
        return parseSRT(text);
      }
      return text;
    },
    [setSubtitleCues]
  );

  /** Load subtitles from a VTT string (received from host) */
  const loadFromVTT = useCallback(
    (vttContent: string) => {
      const cues = parseVTT(vttContent);
      setSubtitleCues(cues);
    },
    [setSubtitleCues]
  );

  /** Update active subtitle for current time with offset compensation */
  const updateSubtitle = useCallback(
    (currentTime: number) => {
      if (!subtitleEnabled) {
        setActiveSubtitle(null);
        return;
      }
      const adjustedTime = Math.max(0, currentTime + (subtitleOffset || 0));
      const active = getActiveCue(subtitleCues, adjustedTime);
      setActiveSubtitle(active);
    },
    [subtitleCues, subtitleEnabled, subtitleOffset, setActiveSubtitle]
  );

  return { loadFile, loadFromVTT, updateSubtitle };
}
